// Tela de Parâmetros do Sistema (issue #6): rodapé da tabela de preços
// exportada (issue #25) e tempos de referência por etapa.

import {
  ETAPAS_COM_TEMPO,
  tempoReferencia,
} from "../../domain/tempos-referencia.ts";
import { rotuloEtapa, type Etapa } from "../../domain/etapas.ts";
import type { EntradaTempoConfig } from "../../comandos/config.ts";
import type { Store } from "../store.ts";
import { clear, el, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderParametros(
  container: HTMLElement,
  store: Store,
): () => void {
  const render = (): void => {
    clear(container);
    const cfg = store.get().config;

    // --- rodapé da tabela de preços exportada ---
    const erroRod = el("p", { class: "form-erro", role: "alert" });
    const slogan = el("input", { type: "text", "aria-required": "true" }) as HTMLInputElement;
    slogan.value = cfg.rodapeExport.slogan;
    const whats = el("input", {
      type: "text",
      placeholder: "(41) 99999-0000",
      "aria-required": "true",
    }) as HTMLInputElement;
    whats.value = cfg.rodapeExport.whatsapp;
    const formRod = el(
      "form",
      { class: "card form-inline" },
      el("h2", {}, "Rodapé da Tabela de Preços Exportada"),
      el(
        "p",
        { class: "muted" },
        "Aparece no pé do PDF e da imagem gerados na tela Tabela de Preços.",
      ),
      el("label", {}, "Slogan", el("span", { class: "campo-obrigatorio", "aria-hidden": "true" }, " *"), slogan),
      el("label", {}, "WhatsApp", el("span", { class: "campo-obrigatorio", "aria-hidden": "true" }, " *"), whats),
      erroRod,
      el("div", { class: "form-actions" }, botao("primario", { type: "submit" }, "Salvar")),
    );
    formRod.addEventListener("submit", (ev) => {
      ev.preventDefault();
      erroRod.textContent = "";
      criarAcaoComando(store, erroRod)({
        agregado: "config",
        cmd: { tipo: "rodapeExport", slogan: slogan.value, whatsapp: whats.value },
      });
    });

    // --- data de referência do faturamento ---
    const erroDf = el("p", { class: "form-erro", role: "alert" });
    const df = el("select", {}) as HTMLSelectElement;
    for (const [v, rot] of [
      ["coleta", "Data da Coleta do Lote"],
      ["devolucao", "Data da Devolução do Lote"],
    ] as const) {
      const o = el("option", { value: v }, rot) as HTMLOptionElement;
      if (cfg.dataFaturamento === v) o.selected = true;
      df.append(o);
    }
    const formDf = el(
      "form",
      { class: "card form-inline" },
      el("h2", {}, "Data de Referência do Faturamento"),
      el(
        "p",
        { class: "muted" },
        "Qual data do lote define em que fatura ele entra e qual preço é " +
          "aplicado. A quantidade cobrada é sempre a devolvida. Peça que volta " +
          "de retidas usa a data do lote em que veio de carona.",
      ),
      el("label", {}, "Base", df),
      erroDf,
      el(
        "div",
        { class: "form-actions" },
        botao("primario", { type: "submit" }, "Salvar"),
      ),
    );
    formDf.addEventListener("submit", (ev) => {
      ev.preventDefault();
      erroDf.textContent = "";
      criarAcaoComando(store, erroDf)({
        agregado: "config",
        // O `<select>` só tem estas duas opções; estreitar aqui é total e
        // dispensa um `as` (ADR-0013).
        cmd: {
          tipo: "dataFaturamento",
          modo: df.value === "devolucao" ? "devolucao" : "coleta",
        },
      });
    });

    // --- tempos de referência por etapa ---
    const erroTmp = el("p", { class: "form-erro", role: "alert" });
    const tmpLinhas = new Map<
      Etapa,
      { pecas: HTMLInputElement; valor: HTMLInputElement; unidade: HTMLSelectElement }
    >();
    const linhasTmp = ETAPAS_COM_TEMPO.map((etapa) => {
      const atual = tempoReferencia(store.get(), etapa);
      // exibe em horas quando o tempo por peça é longo; senão em minutos
      const emHoras = atual != null && atual.minutos / atual.pecas >= 60;

      const pecas = el("input", {
        type: "number",
        min: "0",
        step: "any",
        placeholder: "—",
      }) as HTMLInputElement;
      const valor = el("input", {
        type: "number",
        min: "0",
        step: "any",
        placeholder: "—",
      }) as HTMLInputElement;
      const unidade = el("select", {}) as HTMLSelectElement;
      for (const [v, rot] of [
        ["min", "Minutos"],
        ["h", "Horas"],
      ] as const) {
        const o = el("option", { value: v }, rot) as HTMLOptionElement;
        if ((emHoras ? "h" : "min") === v) o.selected = true;
        unidade.append(o);
      }
      if (atual != null) {
        pecas.value = String(atual.pecas);
        valor.value = String(emHoras ? atual.minutos / 60 : atual.minutos);
      }

      tmpLinhas.set(etapa, { pecas, valor, unidade });
      return el(
        "label",
        {},
        rotuloEtapa(etapa),
        el(
          "span",
          { class: "sufixo tempo-ref" },
          pecas,
          " peças em ",
          valor,
          unidade,
        ),
      );
    });
    const formTmp = el(
      "form",
      { class: "card form-inline" },
      el("h2", {}, "Tempos de Referência por Etapa"),
      el(
        "p",
        { class: "muted" },
        "Produtividade esperada de cada etapa: quantas peças em quanto tempo " +
          "(ex.: 100 peças em 45 minutos). Deixe os dois campos em branco para " +
          "não definir.",
      ),
      ...linhasTmp,
      erroTmp,
      el("div", { class: "form-actions" }, botao("primario", { type: "submit" }, "Salvar")),
    );
    formTmp.addEventListener("submit", (ev) => {
      ev.preventDefault();
      erroTmp.textContent = "";
      const entradas: EntradaTempoConfig[] = [];
      for (const [etapa, campos] of tmpLinhas) {
        const pTxt = campos.pecas.value.trim();
        const vTxt = campos.valor.value.trim();
        if (pTxt === "" && vTxt === "") {
          entradas.push({ etapa, entrada: null });
          continue;
        }
        if (pTxt === "" || vTxt === "") {
          erroTmp.textContent = `${rotuloEtapa(etapa)}: preencha peças e duração, ou deixe os dois em branco.`;
          return;
        }
        entradas.push({
          etapa,
          entrada: {
            pecas: Number(pTxt),
            valor: Number(vTxt),
            unidade: campos.unidade.value === "h" ? "h" : "min",
          },
        });
      }
      criarAcaoComando(store, erroTmp)({
        agregado: "config",
        cmd: { tipo: "temposReferencia", entradas },
      });
    });

    // --- Dashboard Operacional: período padrão e volume mínimo do ranking ---
    const erroDash = el("p", { class: "form-erro", role: "alert" });
    const mesesDash = el("input", { type: "number", min: "0", max: "24", step: "1" }) as HTMLInputElement;
    mesesDash.value = String(cfg.mesesPadraoDashboard);
    const volumeMin = el("input", { type: "number", min: "1", max: "100000", step: "1" }) as HTMLInputElement;
    volumeMin.value = String(cfg.volumeMinimoRanking);
    const numeroDe = (i: HTMLInputElement): number => (i.value.trim() === "" ? NaN : Number(i.value));
    const formDash = el(
      "form",
      { class: "card form-inline" },
      el("h2", {}, "Dashboard Operacional"),
      el(
        "p",
        { class: "muted" },
        "Ao abrir, o Dashboard mostra os meses inteiros anteriores informados aqui mais o mês corrente. " +
          "No ranking por %, item com menos peças que o volume mínimo vai para o fim, esmaecido.",
      ),
      el("label", {}, "Meses Anteriores", mesesDash),
      el("label", {}, "Volume Mínimo no Ranking (peças)", volumeMin),
      erroDash,
      el("div", { class: "form-actions" }, botao("primario", { type: "submit" }, "Salvar")),
    );
    formDash.addEventListener("submit", (ev) => {
      ev.preventDefault();
      erroDash.textContent = "";
      const acao = criarAcaoComando(store, erroDash);
      const meses = numeroDe(mesesDash);
      const pecas = numeroDe(volumeMin);
      // um depois do outro: o 2º não apaga o erro do 1º
      void acao({ agregado: "config", cmd: { tipo: "mesesPadraoDashboard", meses } }, undefined, () => {
        void acao({ agregado: "config", cmd: { tipo: "volumeMinimoRanking", pecas } });
      });
    });
    container.append(formDf, formRod, formTmp, formDash);

    // Só leitura no Módulo Configuração (issue #71): desabilita os 4
    // formulários inteiros, em vez de replicar a checagem campo a campo.
    if (!podeEditarModulo(store, "configuracao")) {
      for (const campo of container.querySelectorAll("input, select, button")) {
        (campo as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled = true;
      }
    }
  };

  render();
  return store.subscribe(render);
}
